import { useTenantOptional } from './TenantContext';
import { useIsEditable } from './useIsEditable';
import { useTenantEditability } from '../api/useTenantEditability';

/**
 * WAVE G — the editability bit that ReadOnlyGuard / WriteButton / ReadOnlyBanner
 * consult when a TENANT is in context.
 *
 * Today `useIsEditable()` is purely ENV-level (currentEnv === editableEnv). With
 * per-tenant authoring homes, a tenant is editable HERE iff:
 *
 *     env-level editable   AND   this env is the tenant's authoring home
 *     (useIsEditable())          (useTenantEditability().editableHere)
 *
 * The env-level gate stays the OUTER fence: a non-editable deployment (or
 * browsing a remote env) is never editable, regardless of homing. The per-tenant
 * gate can only make it MORE restrictive (a tenant homed elsewhere is read-only
 * even on the editable deployment) — never less.
 *
 * BACKWARD COMPAT — flag OFF / no tenant / route absent
 * -----------------------------------------------------
 * - No active tenant (admin pages, tenant picker): we fall back to PURE env-level
 *   editability — identical to today. There's no tenant to gate.
 * - Backend feature OFF or endpoint missing (404): useTenantEditability degrades
 *   to `editableHere = env-level useIsEditable()` and `isDefaultHome = true`, so
 *   the AND below collapses to exactly the env-level bit. Byte-identical to today.
 * - Default-data path (flag ON, tenant not yet homed → resolves dev01): on the
 *   editable env editableHere is true, so behavior is unchanged until a tenant is
 *   explicitly moved.
 *
 * Fail-closed while loading is inherited from useIsEditable (false during
 * /api/app-env load) — we never AND a `true` we don't have.
 */

export interface ActiveTenantEditable {
  /** Final editability decision for the active tenant (or env-level if none). */
  editable: boolean;
  /** The active tenant code in context ('' when none — admin/picker pages). */
  tenantCode: string;
  /** The tenant's authoring home env (or the current env when none/degraded). */
  homeEnv: string;
  /** The env the UI is currently pointed at. */
  currentEnv: string;
  /** True when this env IS the tenant's home (ignores the env-level gate). */
  editableHere: boolean;
  /** True when the tenant has no explicit home row (implicit dev01 / degraded). */
  isDefaultHome: boolean;
  /** Non-null when a home handoff is mid-flight. */
  migratingTo: string | null;
}

export function useActiveTenantEditable(): ActiveTenantEditable {
  // Non-throwing read: a write-affordance can render outside a TenantProvider
  // (tenant picker, unit test). No provider → no active tenant → env-level.
  const activeTenantCode = useTenantOptional()?.activeTenantCode ?? '';
  const envEditable = useIsEditable();
  // Pass undefined when there's no tenant so the hook short-circuits to the
  // env-level fallback (no query fired, isDefaultHome=true).
  const tenant = useTenantEditability(activeTenantCode || undefined);

  // No tenant in context → pure env-level (today's behavior on admin/picker).
  if (!activeTenantCode) {
    return {
      editable: envEditable,
      tenantCode: '',
      homeEnv: tenant.currentEnv,
      currentEnv: tenant.currentEnv,
      editableHere: envEditable,
      isDefaultHome: true,
      migratingTo: null,
    };
  }

  // Tenant in context: AND the env-level gate with the per-tenant home gate.
  // When the backend is off/absent, editableHere already equals envEditable, so
  // `envEditable && editableHere` === `envEditable` — unchanged from today.
  return {
    editable: envEditable && tenant.editableHere,
    tenantCode: activeTenantCode,
    homeEnv: tenant.homeEnv,
    currentEnv: tenant.currentEnv,
    editableHere: tenant.editableHere,
    isDefaultHome: tenant.isDefaultHome,
    migratingTo: tenant.migratingTo,
  };
}

export default useActiveTenantEditable;
